'use client'

import { useState } from 'react'
import {
  Bell,
  BellOff,
  BookOpen,
  Calendar,
  Megaphone,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { portalNavTriggerClassName } from './portal-nav-styles'

type PortalNotification = {
  id: string
  icon: LucideIcon
  iconClassName: string
  title: string
  body: string
  time: string
  read: boolean
}

// Placeholder notifications — will be replaced by the backend notifications feed.
const initialNotifications: PortalNotification[] = [
  {
    id: '1',
    icon: Megaphone,
    iconClassName: 'bg-accent text-primary',
    title: 'Cruise Ambassador Training',
    body: 'Registration is open for the next 2-hour certification course.',
    time: '2h ago',
    read: false,
  },
  {
    id: '2',
    icon: BookOpen,
    iconClassName: 'bg-brand-blue/10 text-brand-blue',
    title: 'New webinar: Mastering Group Travel',
    body: 'Earn 500 Genius Points — starts Jul 16 at 3:00 PM.',
    time: '5h ago',
    read: false,
  },
  {
    id: '3',
    icon: Calendar,
    iconClassName: 'bg-secondary text-secondary-foreground',
    title: 'Ensemble Skyline — register now',
    body: '6 cities across the US + Canada. Limited capacity.',
    time: '1d ago',
    read: false,
  },
  {
    id: '4',
    icon: Sparkles,
    iconClassName: 'bg-brand-blue/10 text-brand-blue',
    title: 'Ensemble in the News',
    body: 'The network enters phase 3 of its refresh.',
    time: '2d ago',
    read: true,
  },
]

export function NotificationsMenu() {
  const [items, setItems] = useState(initialNotifications)
  const unread = items.filter((n) => !n.read).length

  const markAllRead = () => setItems((prev) => prev.map((n) => ({ ...n, read: true })))
  const markRead = (id: string) =>
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-lg"
          className={`${portalNavTriggerClassName} relative`}
          aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        >
          <Bell className="size-6" />
          {unread > 0 && (
            <span className="bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold">
              {unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        className="w-[22rem] max-w-[calc(100vw-2rem)] p-0"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3>Notifications</h3>
          <button
            type="button"
            onClick={markAllRead}
            disabled={unread === 0}
            className="text-link link-hover-underline text-sm font-semibold disabled:pointer-events-none disabled:opacity-50"
          >
            Mark all read
          </button>
        </div>

        {items.length === 0 ? (
          <div className="text-muted-foreground flex flex-col items-center gap-2 px-4 py-10 text-center text-sm">
            <BellOff className="size-6" />
            You’re all caught up.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {items.map((n) => {
              const Icon = n.icon
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => markRead(n.id)}
                  className={cn(
                    'hover:bg-interaction-neutral flex w-full items-start gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0',
                    !n.read && 'bg-accent/60',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-9 shrink-0 items-center justify-center rounded-full',
                      n.iconClassName,
                    )}
                  >
                    <Icon className="size-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm leading-snug font-semibold">{n.title}</p>
                      {!n.read && (
                        <span
                          className="bg-primary mt-1 size-2 shrink-0 rounded-full"
                          aria-label="Unread"
                        />
                      )}
                    </div>
                    <p className="text-muted-foreground mt-0.5 text-sm">{n.body}</p>
                    <p className="text-muted-foreground mt-1 text-sm">{n.time}</p>
                  </div>
                </button>
              )
            })}
          </div>
        )}

        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-interaction-neutral hover:text-foreground w-full"
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
